import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, QuantitySelectorUpdateEvent, DiscountUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * @typedef {import('./utilities').TextComponent} TextComponent
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLTableRowElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 * @property {HTMLElement} progressBar
 */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #lastItemCount = null; // Para detectar transiciones

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = lineItemRow.querySelector('text-component');
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  updateQuantity({ line, quantity, action }) {
    const marker = cartPerformance.createStartingMarker(`${action}:user-action`);
    this.#disableCartItems();

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    this.refs.cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((res) => res.json())
      .then((parsed) => {
        resetShimmer(this);

        if (parsed.errors) {
          this.#handleCartError(line, parsed);
          return;
        }

        // ❌ Antes: Se disparaban eventos aquí.
        // if (isNowEmpty && !wasEmpty) { ... }
        // else if (!isNowEmpty && wasEmpty) { ... }
        // else { ... }

        // ✅ Ahora: Llama directamente a la función unificada
        // Esta función manejará el renderizado y el sistema de regalos
        debouncedUnifiedCartUpdate();
      })
      .catch((err) => console.error(err))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(marker);
      });
}


  // NUEVO: Metodo para manejar estado de carrito vacio
  #handleEmptyCart() {
    // Limpiar referencias locales
    this.refs.cartItemRows = [];
    
    // Actualizar contador visual si existe
    const cartCount = document.querySelector('.cart-count');
    if (cartCount) {
      cartCount.textContent = '0';
      cartCount.classList.add('hidden'); // Ocultar si el carrito esta vacio
    }

    // Limpiar total visual
    if (this.refs.cartTotal) {
      this.refs.cartTotal.textContent = '€0,00';
    }

    // Resetear barra de progreso si existe
    if (this.refs.progressBar) {
      this.refs.progressBar.style.transform = 'scaleX(0)';
    }
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError(line, parsed) {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    const errorEl = this.refs[`cartItemError-${line}`];
    const errorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (errorEl && errorContainer) {
      errorEl.textContent = parsed.errors ?? '';
      errorContainer.classList.remove('hidden');
    }
  }

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
        // ✅ Solución: Disparar un evento unificado para recargar el carrito.
        document.dispatchEvent(new CustomEvent('cart:refresh', {
            detail: {
                source: 'cart-items-component:discount-update'
            }
        }));
        return;
    }

    if (event.target === this) return;

    // MEJORADO: Manejar evento de carrito vacio
    if (event.type === 'cart:emptied') {
      this.#handleEmptyCart();
      return;
    }

    const cartItemsHtml = event.detail.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphSection(cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  #morphSection(html) {
    if (typeof morphSection === 'function') {
      morphSection(this.sectionId, html);
    }
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}